import { createPlaceholderKeys, withKeys } from "@/common/helpers/list-keys";
import { openInNewTab } from "@/common/helpers/open-in-new-tab";
import { cn } from "@/common/helpers/utils";
import { useDevice } from "@/common/hooks/use-device";
import { RowData, flexRender } from "@tanstack/react-table";
import { ChevronRight, SearchXIcon, ServerCrashIcon } from "lucide-react";
import { ComponentType, Fragment, ReactNode, createElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "wouter";
import { RowWithIdentifier } from "../data-table/_helpers/row-identifier";
import { GroupDetails } from "../data-table/predefined/_components/base-predefined-table.types";
import { DataTableInstance, DataTableRow, ImplementedColumnDef } from "../data-table/types";
import { Button, ButtonColorVariant, ButtonSize, ButtonVariant } from "./button";
import { Loader, LoaderSize } from "./loader";
import { TableBody as ShadCNTableBody, TableCell, TableRow } from "./table";
import { RowAction } from "./table-row-actions";

type TableBody<TData extends RowData & RowWithIdentifier> = {
  table: DataTableInstance<TData>;
  isLoading?: boolean;
  hasError?: boolean | Error | null;
  children?: ReactNode;
  onRetry?: () => void;
  subComponent?: ComponentType<{ row: DataTableRow<TData> }>;
  rowClick?: (row: DataTableRow<TData>) => void;
  rowNavigateFn?: (row: DataTableRow<TData>) => string | undefined;
  rowActions?: (row: DataTableRow<TData>) => RowAction[];
  groupByCallback?: (rows: TData[]) => (GroupDetails<TData> | TData)[];
};

const resolveRouterPath = (path: string) => (path.startsWith("~") ? path.slice(1) : path);

export function TableBody<TData extends RowData & RowWithIdentifier>({
  table,
  isLoading,
  hasError,
  children,
  onRetry,
  subComponent,
  rowClick,
  rowNavigateFn,
  rowActions,
  groupByCallback,
}: TableBody<TData>) {
  const { isMobileOrTablet } = useDevice();
  const [expandedActionRows, setExpandedActionRows] = useState<Set<string>>(() => new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => new Set());

  const columnsLength = table?.getAllColumns().length;

  if (!columnsLength) {
    throw new Error("TableBody: No columns found in the table.");
  }

  const processedTableRows =
    groupByCallback && table.getRowModel().rows
      ? (() => {
          const originalData = table.getRowModel().rows.map((row) => row.original);
          const groupedData = groupByCallback(originalData);

          return groupedData
            .map((item) => {
              if ("data" in item && Array.isArray(item.data)) {
                return item as GroupDetails<TData>;
              } else {
                const tableRow = table.getRowModel().rows.find((row) => row.original === item);
                return tableRow ? tableRow : null;
              }
            })
            .filter((rowOrGroup) => rowOrGroup !== null);
        })()
      : table.getRowModel().rows;

  if (isLoading) {
    return (
      <ShadCNTableBody key="table-body-loading">
        <TableLoading columnsLength={columnsLength} />
      </ShadCNTableBody>
    );
  }

  if (hasError) {
    return (
      <ShadCNTableBody key="table-body-error">
        <TableError columnsLength={columnsLength} onRetry={onRetry} />
      </ShadCNTableBody>
    );
  }

  if (children) {
    return <ShadCNTableBody key="table-body">{children}</ShadCNTableBody>;
  }

  if (!isLoading && table?.getRowModel().rows?.length === 0) {
    return (
      <ShadCNTableBody key="table-body-empty">
        <TableEmpty columnsLength={columnsLength} />
      </ShadCNTableBody>
    );
  }

  const handleRowClick = (row: DataTableRow<TData>) => {
    if (isMobileOrTablet && rowActions && rowActions(row).length > 0) {
      setExpandedActionRows((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(row.id)) {
          newSet.delete(row.id);
        } else {
          newSet.add(row.id);
        }
        return newSet;
      });
      return;
    }

    if (rowClick) {
      rowClick(row);
    }
  };

  const handleGroupClick = (groupName: string) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupName)) {
        newSet.delete(groupName);
      } else {
        newSet.add(groupName);
      }
      return newSet;
    });
  };

  const renderRegularRow = (row: DataTableRow<TData>, keyPrefix: string = "", indentColumnIndexes: number[] = []) => {
    const isActionRowExpanded = expandedActionRows.has(row.id);
    const actions = rowActions ? rowActions(row) : [];
    const showMobileActions = isMobileOrTablet && actions.length > 0 && isActionRowExpanded;
    const isDeleted = Boolean(row.original.deletedAt);

    return (
      <Fragment key={`${keyPrefix}${row.id}`}>
        <TableRow
          key={`${keyPrefix}-row-${row.id}`}
          data-state={row.getIsSelected() && "selected"}
          className={cn(
            (rowClick || rowNavigateFn || (isMobileOrTablet && actions.length > 0)) && "cursor-pointer",
            isDeleted && "text-muted-foreground",
          )}
          onMouseDown={(e) => {
            if (e.button === 1) {
              const url = rowNavigateFn ? rowNavigateFn(row) : undefined;
              if (url) e.preventDefault();
            }
          }}
          onClick={(e) => {
            const url = rowNavigateFn ? rowNavigateFn(row) : undefined;
            if (url && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              openInNewTab(resolveRouterPath(url));
              return;
            }
            handleRowClick(row);
          }}
          onAuxClick={(e) => {
            if (e.button !== 1) return;
            const url = rowNavigateFn ? rowNavigateFn(row) : undefined;
            if (url) {
              e.preventDefault();
              openInNewTab(resolveRouterPath(url));
            }
          }}
        >
          {row.getVisibleCells().map((cell, cellIndex) => {
            const columnDef = cell.column.columnDef as ImplementedColumnDef<TData>;
            const cellClassName = "meta" in columnDef ? columnDef.meta?.cellClassName : undefined;
            const rawCellContent = flexRender(columnDef.cell, cell.getContext());
            const cellContent = Array.isArray(rawCellContent)
              ? withKeys(rawCellContent, (_, index) => `${cell.id}-${index}`).map(({ key, item: child }) => (
                  <Fragment key={key}>{child}</Fragment>
                ))
              : rawCellContent;

            // Determine if this cell should be wrapped with a link
            const hasLink = columnDef.link || columnDef.linkFn;
            const linkUrl = columnDef.linkFn ? columnDef.linkFn(row.original) : columnDef.link;
            const linkType = columnDef.linkType || "content";

            const cellElement = (
              <div
                className={cn(
                  "flex min-h-full items-center",
                  columnDef.align === "right" && "justify-end",
                  columnDef.align === "center" && "justify-center",
                  columnDef.align === "left" && "justify-start",
                  indentColumnIndexes.includes(cellIndex) && "pl-4",
                )}
              >
                {hasLink && linkType === "content" && linkUrl ? (
                  <Link to={linkUrl} className="text-secondary underline-offset-4 hover:underline">
                    {cellContent}
                  </Link>
                ) : (
                  cellContent
                )}
              </div>
            );

            return (
              <TableCell
                key={cell.id}
                className={cn(
                  "p-0", // Remove default padding to let the link fill completely
                  hasLink && linkType === "cell" && "group/cell",
                )}
              >
                {hasLink && linkType === "cell" && linkUrl ? (
                  <Link
                    to={linkUrl}
                    className={cn(
                      "flex h-full w-full items-center px-4 py-2 transition-all duration-200",
                      "hover:bg-primary/5 cursor-pointer",
                      "group-hover/cell:bg-primary/10",
                      "hover:border-primary/20 border-l-2 border-transparent",
                      cellClassName,
                    )}
                  >
                    <div
                      className={cn(
                        "flex w-full items-center",
                        columnDef.align === "right" && "justify-end",
                        columnDef.align === "center" && "justify-center",
                        columnDef.align === "left" && "justify-start",
                      )}
                    >
                      {cellContent}
                    </div>
                  </Link>
                ) : (
                  <div className={cn("h-full px-4 py-2", cellClassName)}>{cellElement}</div>
                )}
              </TableCell>
            );
          })}
        </TableRow>
        {subComponent && row.getIsExpanded() && (
          <TableRow key={`expanded-${row.id}`}>
            <TableCell colSpan={columnsLength}>{createElement(subComponent, { row })}</TableCell>
          </TableRow>
        )}
        {showMobileActions && (
          <TableRow key={`actions-${row.id}`}>
            <TableCell colSpan={columnsLength} className="p-0">
              <div
                className={cn(
                  "flex flex-row items-center justify-between [&>*:not(:first-child)]:border-l",
                  "[&>*:not(:first-child)]:rounded-l-none [&>*:not(:last-child)]:rounded-r-none [&>*:not(:last-child)]:border-r",
                )}
              >
                {withKeys(actions, (action, index) => `${action.label}-${index}`).map(({ key, item: action }) => (
                  <Button
                    key={key}
                    variant={ButtonVariant.Filled}
                    colorVariant={action.variant ?? ButtonColorVariant.Primary}
                    className="flex grow flex-row items-center justify-center rounded-none"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      action.onClick();
                    }}
                    disabled={action.disabled}
                  >
                    {action.icon}
                    <span className="ml-2 hidden sm:inline-block">{action.label}</span>
                  </Button>
                ))}
              </div>
            </TableCell>
          </TableRow>
        )}
      </Fragment>
    );
  };

  const renderGroupRow = (group: GroupDetails<TData>) => {
    const isGroupExpanded = expandedGroups.has(group.name);

    return (
      <Fragment key={`group-${group.name}`}>
        <TableRow className="cursor-pointer" onClick={() => handleGroupClick(group.name)}>
          <TableCell className="h-full p-0 px-4 py-2">
            <Button
              variant={ButtonVariant.Ghost}
              colorVariant={ButtonColorVariant.Secondary}
              size={ButtonSize.Sm}
              className="h-8 w-8 p-0"
              onClick={(e) => {
                handleGroupClick(group.name);
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              <ChevronRight
                className={`h-4 w-4 transition-transform ${isGroupExpanded ? "rotate-90 transform" : ""}`}
              />
            </Button>
          </TableCell>
          <TableCell>
            <div className="flex flex-row items-center gap-1">
              <div>{group.name}</div>
              <div className="text-muted-foreground flex items-center justify-center">({group.data.length})</div>
            </div>
          </TableCell>
          {createPlaceholderKeys(columnsLength - 2, "empty").map((key) => (
            <TableCell key={key}></TableCell>
          ))}
        </TableRow>

        {isGroupExpanded &&
          group.data.map((child: TData) => {
            const tableRow = table.getRowModel().rows.find((row) => row.original === child);
            if (!tableRow) return null;

            return renderRegularRow(tableRow, `group-${group.name}`, group.indentColumnIndexes);
          })}
      </Fragment>
    );
  };

  return (
    <ShadCNTableBody key="table-body">
      {processedTableRows.map((rowOrGroup) => {
        if ("data" in rowOrGroup && Array.isArray(rowOrGroup.data)) {
          return renderGroupRow(rowOrGroup);
        } else {
          return renderRegularRow(rowOrGroup as DataTableRow<TData>);
        }
      })}
    </ShadCNTableBody>
  );
}
export function TableLoading({ columnsLength }: { columnsLength: number }) {
  return (
    <TableRow>
      <TableCell colSpan={columnsLength} className="h-32 text-center">
        <div className="flex flex-col items-center gap-2">
          <Loader size={LoaderSize.Medium} />
        </div>
      </TableCell>
    </TableRow>
  );
}

export function TableError({ columnsLength, onRetry }: { columnsLength: number; onRetry?: () => void }) {
  const { t } = useTranslation("common");

  return (
    <TableRow>
      <TableCell colSpan={columnsLength} className="h-32 py-8 text-center">
        <div className="flex flex-col items-center gap-4">
          <div className="bg-error/20 rounded-full p-3">
            <ServerCrashIcon className="text-error h-8 w-8" />
          </div>
          <div className="space-y-2">
            <p className="font-medium">{t("ErrorLoadingData")}</p>
            <p className="text-muted-foreground text-sm">{t("SomethingWentWrongWhileRetrievingData")}</p>
          </div>
          {onRetry && (
            <button
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-4 py-2 text-sm font-medium transition-colors"
              onClick={onRetry}
            >
              {t("TryAgain")}
            </button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

export function TableEmpty({ columnsLength }: { columnsLength: number }) {
  const { t } = useTranslation("common");
  return (
    <TableRow>
      <TableCell colSpan={columnsLength} className="h-32 text-center">
        <div className="flex flex-col items-center gap-2">
          <div className="bg-muted flex h-12 w-12 items-center justify-center rounded-full">
            <SearchXIcon className="text-muted-foreground h-6 w-6" />
          </div>
          <div className="space-y-1">
            <p className="font-medium">{t("NoDataFound")}</p>
            <p className="text-muted-foreground text-sm">{t("NoResultsMatchYourCurrentFilters")}</p>
          </div>
        </div>
      </TableCell>
    </TableRow>
  );
}
